import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsChevronDown, BsChevronRight } from "react-icons/bs";
import { useWorklist } from "./WorklistContext";

/**
 * The reusable frame for one section of "Today's worklist" (KAN-2553).
 *
 * Maria: "Today's worklist encapsulates all other sections, and each one will
 * have its subheader, such as Critical Alerts." So the frame is built once,
 * here, and KAN-2555 (reminders) and KAN-2554 drop into it: collapsible header
 * with its own count badge, and the three states kept VISUALLY DISTINCT.
 *
 * The three states matter more than they look. Collapsing a failed load into
 * "there are no results" would tell a coach, first thing in the morning, that
 * nothing about their critical clinical alerts needs attention. So on error the
 * count badge is not rendered at all — a badge showing 0 is the same lie in
 * smaller type.
 */

export interface WorklistSectionProps {
	sectionId: string;
	/** i18n key for the sub-header, e.g. HOME.WORKLIST.CRITICAL_ALERTS.TITLE */
	titleKey: string;
	count: number;
	isLoading: boolean;
	isError: boolean;
	isEmpty: boolean;
	/** i18n keys. Default to the Requires attention copy (Maria's answer #7). */
	loadingMessageKey?: string;
	errorMessageKey?: string;
	emptyMessageKey?: string;
	/** Whether the section is expanded by default (KAN-2641). Defaults to true. */
	defaultExpanded?: boolean;
	children: React.ReactNode;
}

export const DEFAULT_WORKLIST_MESSAGE_KEYS = {
	loading: "PARTICIPANT.DASHBOARD.REQUIRES_ATTENTION.MESSAGES.LOADING",
	error: "PARTICIPANT.DASHBOARD.REQUIRES_ATTENTION.MESSAGES.ERROR",
	empty: "PARTICIPANT.DASHBOARD.REQUIRES_ATTENTION.MESSAGES.NO_DATA",
};

const WorklistSection = (props: WorklistSectionProps) => {
	const { t } = useTranslation("common");
	const [isExpanded, setIsExpanded] = useState(props.defaultExpanded ?? true);
	const { setSectionCount } = useWorklist();

	// Publish the count so the page owns the running row index and the totals.
	// An errored section publishes 0 rows, not an unknown number of them.
	useEffect(() => {
		setSectionCount(props.sectionId, props.isError ? 0 : props.count);
	}, [props.sectionId, props.count, props.isError, setSectionCount]);

	const contentId = `worklist-section-${props.sectionId}`;
	const showCount = !props.isLoading && !props.isError;

	return (
		<section className="worklist-section">
			<button
				type="button"
				className="worklist-section__header"
				aria-expanded={isExpanded}
				aria-controls={contentId}
				onClick={() => setIsExpanded((expanded) => !expanded)}
			>
				{isExpanded ? <BsChevronDown /> : <BsChevronRight />}
				<span className="worklist-section__title">{t(props.titleKey)}</span>
				{showCount && (
					<span
						className="worklist-section__count"
						data-testid={`${contentId}-count`}
					>
						{props.count}
					</span>
				)}
			</button>

			{/* `k-card` is the platform's white-on-grey content surface (the same
				one UiCard renders). The section HEADER deliberately stays on the
				grey, as in the mockup — only the rows sit on white. */}
			<div
				id={contentId}
				className="k-card"
				hidden={!isExpanded}
			>
				{props.isLoading && (
					<p className="worklist-section__message loading">
						<i className="pi pi-spin pi-spinner message-icon"></i>
						{t(props.loadingMessageKey ?? DEFAULT_WORKLIST_MESSAGE_KEYS.loading)}
					</p>
				)}

				{!props.isLoading && props.isError && (
					<p
						className="worklist-section__message has-error"
						role="alert"
					>
						{t(props.errorMessageKey ?? DEFAULT_WORKLIST_MESSAGE_KEYS.error)}
					</p>
				)}

				{!props.isLoading && !props.isError && props.isEmpty && (
					<p className="worklist-section__message">
						{t(props.emptyMessageKey ?? DEFAULT_WORKLIST_MESSAGE_KEYS.empty)}
					</p>
				)}

				{!props.isLoading && !props.isError && !props.isEmpty && props.children}
			</div>
		</section>
	);
};

export default WorklistSection;
